// Gestos de toque do campus: reconhece toque, toque duplo, toque longo, arrasto, pinça e a
// inércia do arrasto a partir dos eventos de ponteiro. Puro: não toca no DOM, então roda em Node
// (tests/gestures.test.ts). Todas as coordenadas são de tela, em px CSS (clientX/clientY).

/** Um ponteiro (dedo) num instante. `t` em ms, do mesmo relógio para todos os eventos. */
export interface Ponto {
  id: number;
  x: number;
  y: number;
  t: number;
}

export type Gesto =
  /** Um dedo andou: delta desde o último gesto reportado. */
  | { tipo: 'arrasto'; dx: number; dy: number }
  /** Pinça em andamento: `escala` é o fator desde o último evento; (x, y) é o ponto médio; (dx, dy) o quanto ele andou. */
  | { tipo: 'pinca'; escala: number; x: number; y: number; dx: number; dy: number }
  /** Os dois dedos se separaram: a pinça acabou em (x, y). Serve para arredondar o zoom ao soltar. */
  | { tipo: 'fim-pinca'; x: number; y: number }
  /** Soltou um arrasto andando: velocidade do dedo em px/ms, para a inércia. */
  | { tipo: 'solta'; vx: number; vy: number }
  | { tipo: 'toque'; x: number; y: number }
  | { tipo: 'duplo'; x: number; y: number }
  | { tipo: 'longo'; x: number; y: number };

/** Quanto o dedo pode andar sem virar arrasto (px CSS). */
export const SLOP = 10;
/** Dedo parado este tempo vira toque longo (ms). */
export const LONGO_MS = 450;
/** Segundo toque até este tempo depois do primeiro (ms) vira duplo... */
export const DUPLO_MS = 300;
/** ...e só se cair a até este tanto do primeiro (px CSS). */
export const DUPLO_PX = 24;
/** Duração da inércia depois de soltar um arrasto (ms). */
export const INERCIA_MS = 400;
/** Área de toque extra nos personagens (px CSS): o dedo é mais grosso que o cursor. */
export const TOQUE_TOLERANCIA = 12;
/** Velocidade mínima para haver inércia (px/ms); abaixo disso o dedo só parou. */
const VELOCIDADE_MIN = 0.05;
/** Janela de amostras para estimar a velocidade ao soltar (ms). */
const JANELA_MS = 100;
/** Se o dedo ficou parado mais que isso antes de soltar, não há inércia. */
const PARADO_MS = 80;

interface Dedo {
  id: number;
  /** Posição atual. */
  x: number;
  y: number;
  /** Onde o dedo tocou a primeira vez. */
  x0: number;
  y0: number;
  t0: number;
  /** Última posição já reportada num arrasto (para o delta não repetir). */
  ux: number;
  uy: number;
  /** Posições recentes (só movimentos), para estimar a velocidade ao soltar. */
  amostras: Array<{ x: number; y: number; t: number }>;
}

/** Deslocamento acumulado da inércia `decorrido` ms depois de soltar com velocidade (vx, vy) px/ms. */
export function inercia(vx: number, vy: number, decorrido: number): { dx: number; dy: number } {
  // velocidade cai com (1 - k)² até zero: o deslocamento total é v·T/3 e chega a 87,5% na metade do tempo
  const k = Math.min(1, Math.max(0, decorrido) / INERCIA_MS);
  const f = (INERCIA_MS / 3) * (1 - (1 - k) ** 3);
  return { dx: vx * f, dy: vy * f };
}

/**
 * Reconhece os gestos de um ou dois dedos. A entrada são eventos de ponteiro (down/move/up/cancel)
 * e o relógio (`tick`, para o toque longo, que dispara com o dedo ainda parado). Terceiro dedo é ignorado.
 */
export class Gestos {
  private dedos = new Map<number, Dedo>();
  /** Na sessão atual (do primeiro dedo até o último soltar): o dedo já andou o bastante. */
  private movido = false;
  /** Na sessão atual houve dois dedos: não vira toque, longo nem arrasto com inércia. */
  private pinca = false;
  private longo = false;
  private refPinca: { d: number; x: number; y: number } | null = null;
  /** Último toque simples (para formar o duplo). */
  private ultimoToque: { x: number; y: number; t: number } | null = null;

  down(p: Ponto): Gesto[] {
    if (this.dedos.size >= 2 || this.dedos.has(p.id)) return [];
    if (this.dedos.size === 0) {
      this.movido = false;
      this.pinca = false;
      this.longo = false;
    }
    this.dedos.set(p.id, { id: p.id, x: p.x, y: p.y, x0: p.x, y0: p.y, t0: p.t, ux: p.x, uy: p.y, amostras: [{ x: p.x, y: p.y, t: p.t }] });
    if (this.dedos.size === 2) {
      this.pinca = true;
      this.ultimoToque = null;
      this.refPinca = this.medidaPinca();
    }
    return [];
  }

  move(p: Ponto): Gesto[] {
    const dedo = this.dedos.get(p.id);
    if (!dedo) return [];
    dedo.x = p.x;
    dedo.y = p.y;
    dedo.amostras.push({ x: p.x, y: p.y, t: p.t });
    while (dedo.amostras.length > 2 && p.t - dedo.amostras[0]!.t > JANELA_MS) dedo.amostras.shift();
    if (this.dedos.size === 2) return this.pinchMove();
    // o dedo que sobrou de uma pinça não arrasta: senão a câmera pularia ao soltar o outro
    if (this.pinca) {
      dedo.ux = p.x;
      dedo.uy = p.y;
      return [];
    }
    if (!this.movido && Math.hypot(p.x - dedo.x0, p.y - dedo.y0) < SLOP) return [];
    this.movido = true;
    this.ultimoToque = null;
    const out: Gesto[] = [{ tipo: 'arrasto', dx: p.x - dedo.ux, dy: p.y - dedo.uy }];
    dedo.ux = p.x;
    dedo.uy = p.y;
    return out;
  }

  up(p: Ponto): Gesto[] {
    const dedo = this.dedos.get(p.id);
    if (!dedo) return [];
    dedo.x = p.x;
    dedo.y = p.y;
    if (this.dedos.size === 2) {
      // a pinça termina quando sai o primeiro dedo; o que ficou não vira toque
      const m = this.medidaPinca();
      this.dedos.delete(p.id);
      this.refPinca = null;
      this.ultimoToque = null;
      return [{ tipo: 'fim-pinca', x: m.x, y: m.y }];
    }
    this.dedos.delete(p.id);
    if (this.pinca || this.longo) return [];
    if (this.movido) {
      this.ultimoToque = null;
      const v = velocidade(dedo, p.t);
      return Math.hypot(v.vx, v.vy) >= VELOCIDADE_MIN ? [{ tipo: 'solta', vx: v.vx, vy: v.vy }] : [];
    }
    // segurou o bastante mas o relógio não passou por ele (ex.: aba em segundo plano): conta como longo
    if (p.t - dedo.t0 >= LONGO_MS) {
      this.longo = true;
      this.ultimoToque = null;
      return [{ tipo: 'longo', x: p.x, y: p.y }];
    }
    const antes = this.ultimoToque;
    if (antes && p.t - antes.t <= DUPLO_MS && Math.hypot(p.x - antes.x, p.y - antes.y) <= DUPLO_PX) {
      this.ultimoToque = null;
      return [{ tipo: 'duplo', x: p.x, y: p.y }];
    }
    this.ultimoToque = { x: p.x, y: p.y, t: p.t };
    return [{ tipo: 'toque', x: p.x, y: p.y }];
  }

  /** Ponteiro cancelado pelo navegador (ex.: o sistema tomou o gesto): esquece tudo, sem toque. */
  cancel(): Gesto[] {
    const out: Gesto[] = [];
    if (this.pinca && this.refPinca) out.push({ tipo: 'fim-pinca', x: this.refPinca.x, y: this.refPinca.y });
    this.dedos.clear();
    this.refPinca = null;
    this.ultimoToque = null;
    this.movido = false;
    this.pinca = false;
    this.longo = false;
    return out;
  }

  /** Chamado a cada quadro: um dedo parado por LONGO_MS vira toque longo (uma vez por sessão). */
  tick(now: number): Gesto[] {
    if (this.dedos.size !== 1 || this.pinca || this.movido || this.longo) return [];
    const dedo = this.dedos.values().next().value!;
    if (now - dedo.t0 < LONGO_MS) return [];
    this.longo = true;
    this.ultimoToque = null;
    return [{ tipo: 'longo', x: dedo.x, y: dedo.y }];
  }

  private medidaPinca(): { d: number; x: number; y: number } {
    const [a, b] = [...this.dedos.values()] as [Dedo, Dedo];
    return { d: Math.hypot(a.x - b.x, a.y - b.y), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  private pinchMove(): Gesto[] {
    const ref = this.refPinca;
    const m = this.medidaPinca();
    this.refPinca = m;
    if (!ref || ref.d <= 0 || m.d <= 0) return [];
    return [{ tipo: 'pinca', escala: m.d / ref.d, x: m.x, y: m.y, dx: m.x - ref.x, dy: m.y - ref.y }];
  }
}

/** Velocidade do dedo nos últimos JANELA_MS antes de soltar, em px/ms. Zero se ele já tinha parado. */
function velocidade(dedo: Dedo, tSoltou: number): { vx: number; vy: number } {
  const fim = dedo.amostras[dedo.amostras.length - 1];
  if (!fim || tSoltou - fim.t > PARADO_MS) return { vx: 0, vy: 0 };
  const ini = dedo.amostras.find((s) => fim.t - s.t <= JANELA_MS) ?? fim;
  const dt = fim.t - ini.t;
  if (dt < 8) return { vx: 0, vy: 0 };
  return { vx: (fim.x - ini.x) / dt, vy: (fim.y - ini.y) / dt };
}
